import React, {
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import Select, { SingleValue } from 'react-select'
import { FormikContext, FormikContextType } from 'formik'
import { FiAlertCircle } from 'react-icons/fi'
import { IconBaseProps } from 'react-icons'
import Tooltip from '../Tooltip'

type Option = {
  label: string
  value: string
}

interface Props {
  name: string
  placeholder: string
  options?: Option[]
  icon?: React.ComponentType<IconBaseProps>

  /** ✅ Controlado fora do Formik: valor simples (string) */
  value?: string

  isDisabled?: boolean
  isSearchable?: boolean

  /** ✅ Controlado fora do Formik: callback com valor simples (string) */
  onChange?: (value: string) => void
}

type FormikValues = Record<string, unknown>

const InputSelect: React.FC<Props> = ({
  name,
  placeholder,
  options,
  icon: Icon,
  value: valueProp,
  isDisabled = false,
  isSearchable = true,
  onChange: onChangeProp,
}) => {
  const formik = useContext(FormikContext) as
    | FormikContextType<FormikValues>
    | undefined

  const isFormik = !!formik

  const [isFocused, setIsFocused] = useState(false)

  const safeOptions = useMemo<Option[]>(() => {
    if (!Array.isArray(options)) return []

    return options
      .filter((o): o is Option => !!o && typeof o.label === 'string')
      .map((o) => ({
        label: o.label,
        value: String(o.value ?? ''),
      }))
  }, [options])

  const formikValue = isFormik ? String(formik.values?.[name] ?? '') : ''
  const formikTouched = isFormik ? !!formik.touched?.[name] : false
  const formikError = isFormik ? formik.errors?.[name] : undefined

  const rawValue = useMemo(() => {
    if (valueProp !== undefined) return valueProp

    if (!isFormik) return ''

    return formikValue
  }, [formikValue, isFormik, valueProp])

  const selectedOption = useMemo(() => {
    if (!rawValue) return null
    return safeOptions.find((o) => String(o.value) === String(rawValue)) ?? null
  }, [rawValue, safeOptions])

  const hasValue = !!selectedOption
  const isActive = isFocused || hasValue

  const error = formikTouched ? formikError : undefined

  const handleChange = useCallback(
    (option: SingleValue<Option>) => {
      const nextVal = option?.value ?? ''

      if (valueProp === undefined && isFormik) {
        formik.setFieldValue(name, nextVal)

        if (nextVal) formik.setFieldError(name, undefined)
      }

      onChangeProp?.(nextVal)
    },
    [formik, isFormik, name, onChangeProp, valueProp],
  )

  const handleBlur = useCallback(() => {
    if (isFormik) formik.setFieldTouched(name, true)
    setIsFocused(false)
  }, [formik, isFormik, name])

  const borderColorClass = error
    ? 'border-[#C53030]'
    : isFocused
      ? 'border-[#8493F4]'
      : hasValue
        ? 'border-[rgba(132,147,244,0.6)]'
        : 'border-[rgba(174,174,174,0.6)]'

  const textColorClass = isFocused
    ? 'text-gray-900'
    : hasValue
      ? 'text-[#D1D4E2]'
      : 'text-[#666360]'

  const paddingClass = isActive ? 'pt-[22px] pb-[12px]' : 'pt-[16px] pb-[16px]'

  const containerClassName = [
    'relative flex w-full items-center rounded-[4px] border bg-transparent px-[12px] mb-[12px] transition-[border-color,color,padding] duration-200',
    paddingClass,
    borderColorClass,
    textColorClass,
  ]
    .filter(Boolean)
    .join(' ')

  const labelClassName = [
    'absolute left-[12px] top-[16px] pr-[12px] text-[12px] font-bold text-[#666360] transition-all duration-200 pointer-events-none',
    isActive ? '-translate-y-[12px] tracking-[0.1em]' : '',
  ]
    .filter(Boolean)
    .join(' ')

  const iconClassName = [
    'mr-[12px]',
    hasValue ? 'text-[#8493F4]' : 'text-[#666360]',
  ].join(' ')

  return (
    <div className={containerClassName}>
      {isActive && Icon && <Icon size={20} className={iconClassName} />}

      <label htmlFor={name} className={labelClassName}>
        {placeholder}
      </label>

      <div className="w-full">
        <Select<Option, false>
          inputId={name}
          instanceId={name}
          options={safeOptions}
          value={selectedOption}
          isDisabled={isDisabled}
          isSearchable={isSearchable}
          placeholder=""
          onChange={handleChange}
          onFocus={() => setIsFocused(true)}
          onBlur={handleBlur}
          unstyled
          classNames={{
            control: () => 'flex min-h-[32px] w-full bg-transparent',
            valueContainer: () => 'px-0 py-0 text-[#333]',
            placeholder: () => 'text-[#666360]',
            singleValue: () => 'text-[#333]',
            input: () => 'text-[#333]',
            menu: () =>
              'mt-[8px] rounded-[6px] border border-[#e6e6e6] bg-white shadow-[0px_6px_12px_rgba(61,69,67,0.12)]',
            option: ({ isFocused: optionFocused, isSelected }) =>
              [
                'px-[10px] py-[8px] text-[14px]',
                optionFocused ? 'bg-[#F3F4F6]' : '',
                isSelected ? 'bg-[#E5E7EB]' : '',
              ]
                .filter(Boolean)
                .join(' '),
            indicatorsContainer: () => 'gap-[4px]',
            dropdownIndicator: () => 'text-[#666360]',
            clearIndicator: () => 'text-[#666360]',
            noOptionsMessage: () =>
              'px-[10px] py-[8px] text-[12px] text-[#666360]',
          }}
        />
      </div>

      {error && (
        <Tooltip
          title={String(error)}
          className="absolute right-[10px] ml-[12px] h-[20px] [&>svg]:m-0"
        >
          <FiAlertCircle color="#c53030" size="20" />
        </Tooltip>
      )}
    </div>
  )
}

export default InputSelect
